// Drawing upload / delete helpers — Firebase Storage layer for drawings.
//
// Storage layout:
//   /drawings/parts/$partId/$filename     — per-part attachments
//   /drawings/quotes/$quoteId/$filename   — quote-level attachment pool
//                                           (Justin's "traveler" metaphor:
//                                           one pool per quote, each part
//                                           line picks which drawings apply)
//
// Auth is enforced at the Security Rules layer (firebase.storage.rules) — any
// signed-in user can read + write. We still awaitFirebaseUser() before any
// SDK call so the token is attached when the request goes out.
//
// The caller (DrawingUpload / QuoteAttachmentsPanel) is responsible for pushing
// the returned download URL into the matching attachedDrawings[] via updatePart /
// updateQuote — this module stays a thin file-IO seam so it can be reused for
// any future non-Part / non-Quote uploads (e.g. RFQ inbound, vendor RFQs)
// without dragging CRM state in.

import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { storage, awaitFirebaseUser } from './firebase';

// 25MB — matches the firebase.storage.rules cap so client-side rejections
// match the server-side rejection. Keeps the error path consistent regardless
// of which layer catches the oversize file first.
const MAX_BYTES = 25 * 1024 * 1024;

// Drawing formats KMF actually receives from customers + CAD seats. PDF and
// raster previews dominate; STEP/IGES/DXF cover the CAM ingest path. Native
// CAD formats (Parasolid / SolidWorks / Inventor / Fusion / NX/Creo / ACIS)
// land here unviewable for now but stored so the operator can pull them
// down into their CAD seat later. STEP variants are the same ISO 10303-21
// content under different extensions — .p21 in particular is the official
// extension and was rejecting silently before.
const ACCEPTED_EXTENSIONS = [
  // Drawings
  'pdf',
  'png',
  'jpg',
  'jpeg',
  'svg',
  'gif',
  'webp',
  // 2D CAM
  'dxf',
  'dwg',
  // 3D neutral formats (STEP variants are ISO 10303-21)
  'step',
  'stp',
  'p21',
  'iges',
  'igs',
  'stl',
  'sat',
  // Native CAD (stored, not previewed)
  'sldprt',
  'sldasm',
  'sldrt',
  'slddrw',
  'ipt',
  'iam',
  'idw',
  'f3d',
  'prt',
  'asm',
  'x_t',
  'x_b',
  'xt',
  '3dm',
] as const;

export interface UploadedDrawing {
  url: string;       // download URL ready to drop into attachedDrawings
  path: string;      // storage path (for delete later)
  filename: string;
  contentType: string;
  sizeBytes: number;
}

/** Lowercase extension (no dot) for a filename, or '' if none. */
function extOf(filename: string): string {
  const dot = filename.lastIndexOf('.');
  if (dot < 0 || dot === filename.length - 1) return '';
  return filename.slice(dot + 1).toLowerCase();
}

/**
 * Sanitize a user-supplied filename for use as a Storage object name.
 * Strips path separators, control characters, and collapses whitespace to
 * underscores. The Storage SDK technically handles slashes in object names
 * (treats them as folders) — which is exactly what we DON'T want; the part
 * folder is already in the prefix and a drawing called `subdir/foo.pdf`
 * would land in `drawings/parts/$partId/subdir/foo.pdf`.
 */
function sanitizeFilename(name: string): string {
  // Drop path components — keep only the basename.
  const base = name.split(/[/\\]/).pop() || name;
  // Replace whitespace runs with a single underscore; strip control chars
  // and any leftover OS-reserved characters (<>:"|?*). Keep dots so the
  // extension survives.
  return base
    .replace(/[\x00-\x1f\x7f<>:"|?*]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/** Validation gate — throws a friendly Error on rejection. */
function assertAcceptable(file: File): void {
  if (file.size > MAX_BYTES) {
    const mb = (file.size / (1024 * 1024)).toFixed(1);
    throw new Error(
      `"${file.name}" is ${mb}MB — drawings must be under 25MB. ` +
        `Try a lower-res export or send the native CAD file instead.`,
    );
  }
  const ext = extOf(file.name);
  if (!ext) {
    throw new Error(
      `"${file.name}" has no extension. Accepted: ${ACCEPTED_EXTENSIONS.join(', ')}.`,
    );
  }
  if (!(ACCEPTED_EXTENSIONS as readonly string[]).includes(ext)) {
    throw new Error(
      `"${file.name}" (.${ext}) isn't a supported drawing type. ` +
        `Accepted: ${ACCEPTED_EXTENSIONS.join(', ')}.`,
    );
  }
}

/**
 * Internal — shared upload core. Both Part and Quote uploads collapse to this
 * after building their respective storage prefix.
 */
async function uploadToPath(
  prefix: string,
  file: File,
): Promise<UploadedDrawing> {
  await awaitFirebaseUser();
  assertAcceptable(file);

  const cleanName = sanitizeFilename(file.name);
  if (!cleanName) {
    throw new Error(`"${file.name}" has no usable characters after sanitizing.`);
  }
  const path = `${prefix}/${cleanName}`;
  const objRef = storageRef(storage, path);

  // contentType — File.type is what the browser sniffed; fall back to a
  // sensible default for known CAD formats the browser doesn't recognize
  // (DXF/STEP/IGES typically come through as empty-string `type`).
  const contentType = file.type || guessContentType(cleanName);

  const result = await uploadBytes(objRef, file, { contentType });
  const url = await getDownloadURL(result.ref);

  return {
    url,
    path,
    filename: cleanName,
    contentType,
    sizeBytes: file.size,
  };
}

/**
 * Upload a file to /drawings/parts/$partId/$filename. Returns the download
 * URL. Caller is responsible for pushing the URL into the part's
 * attachedDrawings[] via updatePart.
 *
 * Constraints: 25MB max. Accepted: PDF, DXF, STEP/STP, IGES/IGS, PNG/JPG/SVG.
 */
export async function uploadPartDrawing(
  partId: string,
  file: File,
): Promise<UploadedDrawing> {
  return uploadToPath(`drawings/parts/${partId}`, file);
}

/**
 * Upload a file to /drawings/quotes/$quoteId/$filename — the quote-level
 * drawing pool. Justin's "traveler" metaphor: each quote owns a pool of
 * drawings (dragged onto Builder tab, sourced from inbound RFQ, etc.), and
 * each part line picks which drawings from the pool apply to it.
 *
 * Caller pushes the returned URL into `quote.attachedDrawings[]` via
 * updateQuote. Same 25MB + extension validation as Part uploads.
 */
export async function uploadQuoteAttachment(
  quoteId: string,
  file: File,
): Promise<UploadedDrawing> {
  return uploadToPath(`drawings/quotes/${quoteId}`, file);
}

/**
 * Upload an RFQ-pool attachment to /drawings/rfqs/$rfqLocalId/$filename.
 *
 * Used by the RFQ extract panel: when Justin drags drawings/pictures/specs
 * onto the panel, they upload to this pool keyed by a client-minted
 * `rfqLocalId` (UUID held in component state for the lifetime of the
 * in-flight extract). When the user clicks Create Quote, the resulting
 * download URLs are passed to createQuote() as `quote.attachedDrawings`
 * and `quote.fromRfqId` is set to the same rfqLocalId for trace.
 *
 * When the full RFQ-intake pipeline lands and /rfqs/{rfqId} owns canonical
 * IDs, these can be migrated by path prefix. Same 25MB + extension
 * validation as Part uploads.
 */
export async function uploadRfqAttachment(
  rfqLocalId: string,
  file: File,
): Promise<UploadedDrawing> {
  return uploadToPath(`drawings/rfqs/${rfqLocalId}`, file);
}

/**
 * Delete by storage path. Doesn't touch attachedDrawings — caller does.
 * Single helper works for both Part and Quote attachments (path is opaque).
 */
export async function deletePartDrawing(storagePath: string): Promise<void> {
  await awaitFirebaseUser();
  const objRef = storageRef(storage, storagePath);
  await deleteObject(objRef);
}

/**
 * Alias for symmetry with uploadQuoteAttachment — same behavior as
 * deletePartDrawing (path is opaque to Firebase Storage).
 */
export async function deleteQuoteAttachment(storagePath: string): Promise<void> {
  return deletePartDrawing(storagePath);
}

/**
 * Best-effort delete by download URL — derives the storage path from the
 * Firebase Storage URL format and calls deleteObject. Returns silently on
 * failure (the caller has already removed the URL from the canonical list;
 * a leftover blob is a soft leak, not data loss). Used when the caller only
 * has the download URL, not the original storage path — e.g. removing an
 * entry from quote.attachedDrawings[] where we only persisted the URL.
 */
export async function deleteByDownloadUrl(downloadUrl: string): Promise<void> {
  // Firebase Storage download URLs look like:
  //   https://firebasestorage.googleapis.com/v0/b/$bucket/o/$encodedPath?alt=media&token=...
  // We extract $encodedPath, decode it, and feed it to deleteObject.
  try {
    const m = downloadUrl.match(/\/o\/([^?]+)/);
    if (!m) return; // not a Firebase Storage URL — silently skip
    const path = decodeURIComponent(m[1]);
    await awaitFirebaseUser();
    const objRef = storageRef(storage, path);
    await deleteObject(objRef);
  } catch {
    // Object may already be gone, or the URL came from somewhere else. The
    // canonical list is already updated by the caller — a leftover blob is
    // a soft leak, not a correctness bug.
  }
}

/**
 * Last-resort content-type guess for CAD formats the browser doesn't sniff.
 * Used only when File.type is empty — the upload path is correct either way,
 * this just keeps the Storage metadata useful for a future download header.
 */
function guessContentType(filename: string): string {
  const ext = extOf(filename);
  switch (ext) {
    case 'pdf': return 'application/pdf';
    case 'dxf': return 'application/dxf';
    case 'step':
    case 'stp': return 'application/step';
    case 'iges':
    case 'igs': return 'application/iges';
    case 'png': return 'image/png';
    case 'jpg':
    case 'jpeg': return 'image/jpeg';
    case 'svg': return 'image/svg+xml';
    default: return 'application/octet-stream';
  }
}
